import { useState } from "react";
import type { SwmmProject } from "../model/inp/types.ts";
import type { BackdropCalibration } from "../model/inp/backdrop.ts";
import { computeBounds } from "../model/inp/bounds.ts";
import { useT } from "../i18n/hooks.ts";
import { Dialog } from "./Dialog.tsx";
import { DialogActions } from "./DialogActions.tsx";
import { ImportIcon } from "./ImportIcon.tsx";

/** Corners are entered as plain numbers rather than an on-map two-click calibration tool (unlike the real desktop GUI); "use project extent" gives a reasonable starting point to fine-tune from. */
export function BackgroundImageDialog({
  project,
  currentBlob,
  currentCalibration,
  onApply,
  onClose,
}: {
  project: SwmmProject;
  currentBlob: Blob | null | undefined;
  currentCalibration: BackdropCalibration | null;
  onApply: (blob: Blob | null, calibration: BackdropCalibration | null) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [pendingBlob, setPendingBlob] = useState<Blob | null | undefined>(currentBlob);
  const [pendingFileName, setPendingFileName] = useState<string | null>(null);
  const [llx, setLlx] = useState(String(currentCalibration?.lowerLeft.x ?? 0));
  const [lly, setLly] = useState(String(currentCalibration?.lowerLeft.y ?? 0));
  const [urx, setUrx] = useState(String(currentCalibration?.upperRight.x ?? 0));
  const [ury, setUry] = useState(String(currentCalibration?.upperRight.y ?? 0));

  function useProjectExtent() {
    const bounds = computeBounds(project);
    if (!bounds) return;
    setLlx(String(bounds.minX));
    setLly(String(bounds.minY));
    setUrx(String(bounds.maxX));
    setUry(String(bounds.maxY));
  }

  function apply() {
    if (!pendingBlob) {
      onApply(null, null);
      return;
    }
    const calibration: BackdropCalibration = {
      lowerLeft: { x: Number(llx), y: Number(lly) },
      upperRight: { x: Number(urx), y: Number(ury) },
    };
    onApply(pendingBlob, calibration);
  }

  const canApply =
    !!pendingBlob &&
    [llx, lly, urx, ury].every((v) => Number.isFinite(Number(v)) && v.trim() !== "");

  // Plain comparison against props rather than useIsDirty's snapshot approach,
  // since a Blob has no meaningful JSON form to snapshot.
  const dirty =
    pendingBlob !== currentBlob ||
    llx !== String(currentCalibration?.lowerLeft.x ?? 0) ||
    lly !== String(currentCalibration?.lowerLeft.y ?? 0) ||
    urx !== String(currentCalibration?.upperRight.x ?? 0) ||
    ury !== String(currentCalibration?.upperRight.y ?? 0);

  return (
    <Dialog title={t("backgroundImage.title")} onClose={onClose} isDirty={dirty}>
      <div className="dialog-body-stack">
        <div className="options-field">
          <span>{t("backgroundImage.file")}</span>
          {/* Audit finding: the native `<input type="file">` shipped the
              browser's own default "Seleccionar archivo | Ningún archivo"
              chrome — different typography, borders and spacing from every
              other control in the app — and stood out as clearly foreign.
              The <input> stays (it's the only way to open the system file
              picker) but is visually hidden inside this <label>; the label
              itself renders as a real `.button` so it picks up the app's
              own token-driven styling. Focus lands on the still-focusable
              hidden input, and `:focus-within` on the label forwards a
              visible ring back to the button surface. */}
          <label className="button file-picker-button">
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setPendingBlob(file);
                  setPendingFileName(file.name);
                }
              }}
            />
            <ImportIcon />
            <span>{t("backgroundImage.selectFile")}</span>
          </label>
          <span className="file-picker-name">
            {pendingFileName ??
              (pendingBlob
                ? t("backgroundImage.currentImage")
                : t("backgroundImage.noFileSelected"))}
          </span>
        </div>
        {!pendingBlob && <p className="property-panel-hint">{t("backgroundImage.noImage")}</p>}

        <section className="options-section">
          <label className="options-field">
            {t("backgroundImage.lowerLeftX")}
            <input type="number" value={llx} onChange={(e) => setLlx(e.target.value)} />
          </label>
          <label className="options-field">
            {t("backgroundImage.lowerLeftY")}
            <input type="number" value={lly} onChange={(e) => setLly(e.target.value)} />
          </label>
          <label className="options-field">
            {t("backgroundImage.upperRightX")}
            <input type="number" value={urx} onChange={(e) => setUrx(e.target.value)} />
          </label>
          <label className="options-field">
            {t("backgroundImage.upperRightY")}
            <input type="number" value={ury} onChange={(e) => setUry(e.target.value)} />
          </label>
        </section>
        <button className="button" onClick={useProjectExtent}>
          {t("backgroundImage.useProjectExtent")}
        </button>
      </div>
      <DialogActions
        primary={{ label: t("common.save"), onClick: apply, disabled: !canApply }}
        secondary={
          currentBlob
            ? {
                label: t("backgroundImage.remove"),
                onClick: () => onApply(null, null),
                danger: true,
              }
            : undefined
        }
        onClose={onClose}
      />
    </Dialog>
  );
}
